// CustomSections.jsx
import React, { useMemo, useState, useEffect } from "react";
import { Group, Text } from "react-konva";
import CustomRect from "./CustomRect";
import { buildColorMapWithProbing, getColorByTag } from "../Utils/colorUtils";
import api from "../../../../api/axios";
import { isDummyDuct } from "../../../../builders/dummyDucts";

const localStrokeWidth = 0.2;

// The band separator duct gets its width from PlacementOptions, not from the
// scenario's section duct - it is never a placeholder.
const BAND_SEPARATOR_NAME = "Band Cable Duct (vertical separator)";

// Same look as the placeholder enclosure ducts in CustomCabinet
const DUMMY_DUCT_STYLE = { fill: "#CFE0E0", stroke: "#5C9495", strokeWidth: 1.5, dash: [8, 6] };

const CustomSections = React.memo(function CustomSections({
  generatedSections = [],
  ductTop,
  cabinetX = 0,
  cabinetY = 0,
  optimized,
  sectionPlacements = [],
  sections = [],
  showSectionBorders = false,
  projectId,
  devices = [],
  onDeviceHover,
  hiddenIds,
  selectedId,
  selectedIds,      // Set of selected SubGroup ids (one device or a whole section)
  onDeviceSelect
}) {

  const [subGroups, setSubGroups] = useState([]);
  // Load master SubGroups
  useEffect(() => {
    api.get("/subgroup")
      .then(res => setSubGroups(res.data))
      .catch(err => console.error("Failed to load SubGroups:", err));
  }, []);

  // Extract all SubGroupPlacements from sectionPlacements
  const subGroupPlacements = useMemo(() => {
    if (!Array.isArray(sectionPlacements)) return [];
    const all = [];
    for (const sp of sectionPlacements) {
      if (Array.isArray(sp.subGroupPlacements)) {
        all.push(...sp.subGroupPlacements);
      }
    }
    return all;
  }, [sectionPlacements]);

  // Section placements whose duct is a placeholder (no duct selected)
  const dummyDuctPlacementIds = useMemo(() => {
    if (!Array.isArray(sectionPlacements)) return new Set();
    return new Set(
      sectionPlacements.filter((sp) => isDummyDuct(sp.ductTop)).map((sp) => sp.id)
    );
  }, [sectionPlacements]);

  // sectionId → name
  const sectionNameBySectionId = useMemo(() => {
    const map = new Map();
    for (const s of sections) map.set(s.id, s.name ?? s.id);
    return map;
  }, [sections]);

  // sectionPlacementId → display name
  const sectionNameByPlacementId = useMemo(() => {
    const map = new Map();
    for (const sp of sectionPlacements) {
      const name = sectionNameBySectionId.get(sp.referenceId) ??
        `Section ${sp.id}`;
      map.set(sp.id, name);
    }
    return map;
  }, [sectionPlacements, sectionNameBySectionId]);

  const masterDevById = useMemo(() => {
    if (!Array.isArray(devices)) return new Map();

    const map = new Map();
    for (const dv of devices) {
      map.set(dv.id, dv);
    }
    return map;
  }, [devices]);

  const masterSgById = useMemo(() => {
    const map = new Map();
    for (const sg of subGroups) map.set(sg.id, sg);
    return map;
  }, [subGroups]);

  const sgPlacementById = useMemo(() => {
    const map = new Map();
    for (const sgp of subGroupPlacements) map.set(sgp.id, sgp);
    return map;
  }, [subGroupPlacements]);

  // Extract visible placement IDs safely
  const visiblePlacementIds = useMemo(() => {
    const set = new Set();
    for (const gs of generatedSections || []) {
      if (gs.sectionPlacementId) set.add(gs.sectionPlacementId);
    }
    return [...set];
  }, [generatedSections]);

  // Build stable colorMap
  const colorMap = useMemo(() => {
    return buildColorMapWithProbing(visiblePlacementIds, {
      minHueDistance: 22,
      saturation: 65,
      lightness: 70,
    });
  }, [visiblePlacementIds]);

  // Render all sections safely
  const renderedSections = useMemo(() => {
    if (!Array.isArray(generatedSections)) return null;

    return generatedSections.map(section => {
      const pid = section.sectionPlacementId;

      const strokeColor = showSectionBorders
        ? (colorMap.get(pid) ?? "#95a5a6")
        : "transparent";

      const strokeWidth = showSectionBorders ? 7 : localStrokeWidth;

      return (
        <Group
          key={section.id}
          x={section.x + cabinetX}
          y={section.y + cabinetY}
        >
          {/* Section background */}
          <CustomRect
            x={0}
            y={0}
            width={section.width}
            height={section.height}
            fill="#BDBDBE"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
          />

          {/* Devices */}
          {(section.generatedDevices || []).map(device => {

            const master = device.deviceId
              ? masterDevById.get(device.deviceId)
              : null;

            const subGroupId = master?.subGroup?.id;

            if (subGroupId && hiddenIds?.has(subGroupId)) {
              return null;
            }
            const isDuct = device.deviceTag === "DUCT";
            const isRail = device.deviceTag === "RAIL";
            const isDummyDuctDevice =
              isDuct &&
              device.name !== BAND_SEPARATOR_NAME &&
              dummyDuctPlacementIds.has(pid);

            const opacity = device.isPlaceholder ? 0.3 : 1;
            const isSelected = selectedIds
              ? !!subGroupId && selectedIds.has(subGroupId)
              : subGroupId === selectedId;

            const rectStyle = isDummyDuctDevice
              ? DUMMY_DUCT_STYLE
              : {
                fill: device.isPlaceholder
                  ? "transparent"
                  : isDuct
                    ? "#5C9495"
                    : getColorByTag(device.deviceTag),
                stroke: isRail || device.isPlaceholder
                  ? "transparent"
                  : isSelected
                    ? "#0096ff"
                    : "#333",
                strokeWidth: isRail || device.isPlaceholder
                  ? 0
                  : isSelected
                    ? 3
                    : localStrokeWidth,
              };

            return (
              <Group
                key={device.id}
                x={device.position.x - section.x}
                y={device.position.y - section.y}
                opacity={opacity}
                onClick={() => onDeviceSelect?.(subGroupId)}
                onMouseEnter={() => {
                  if (isDummyDuctDevice) {
                    onDeviceHover?.("Placeholder duct – no duct selected in the scenario");
                    return;
                  }
                  const devId = device.deviceId;
                  const master = devId
                    ? masterDevById.get(devId)
                    : null;
                  const info = [
                    master?.subGroup.name ? `Name: ${master.subGroup.name}` : "",
                    master?.subGroup.articleNumber
                      ? `Article: ${master.subGroup.articleNumber}`
                      : "",
                  ]
                    .filter(Boolean)
                    .join("\n");

                  onDeviceHover?.(info);
                }}
                onMouseLeave={() => {
                  onDeviceHover?.(null)
                }}
              >
                <CustomRect
                  x={0}
                  y={0}
                  width={device.width}
                  height={device.height}
                  {...rectStyle}
                />

                {!device.isPlaceholder && !isDuct && !isRail && (
                  <Text
                    text={device.deviceTag}
                    fontSize={12}
                    fill="black"
                    width={device.width}
                    height={device.height}
                    align="center"
                    verticalAlign="middle"
                  />
                )}
              </Group>
            );
          })}
        </Group>
      );
    });
  }, [generatedSections, showSectionBorders, colorMap, cabinetX, cabinetY, masterDevById, hiddenIds, selectedId, selectedIds, onDeviceSelect, onDeviceHover, dummyDuctPlacementIds]);

  return (
    <>
      {renderedSections}
    </>
  );
});

export default React.memo(CustomSections);
